// PersonaNotasSection.jsx

import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Box,
    Stack,
    Typography,
    IconButton,
    List,
    ListItem,
    Divider,
    Button,
    Tooltip,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Menu,
    MenuItem,
    ListItemIcon,
    useTheme,
    Avatar,
    Paper,
    ListItemButton,
    useMediaQuery,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SortIcon from "@mui/icons-material/Sort";
import SearchIcon from "@mui/icons-material/Search";
import CircleIcon from "@mui/icons-material/Circle";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import StrikethroughSIcon from "@mui/icons-material/StrikethroughS";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import FormatQuoteIcon from "@mui/icons-material/FormatQuote";
import TitleIcon from "@mui/icons-material/Title";
import UndoIcon from "@mui/icons-material/Undo";
import RedoIcon from "@mui/icons-material/Redo";
import FormatClearIcon from "@mui/icons-material/FormatClear";
import HorizontalRuleIcon from "@mui/icons-material/HorizontalRule";
import LooksOneIcon from "@mui/icons-material/LooksOne";
import LooksTwoIcon from "@mui/icons-material/LooksTwo";
import Looks3Icon from "@mui/icons-material/Looks3";
import TextFieldsIcon from "@mui/icons-material/TextFields";
import HelpIcon from "@mui/icons-material/Help";
import EmojiEmotionsIcon from "@mui/icons-material/EmojiEmotions";
import AlternateEmailIcon from "@mui/icons-material/AlternateEmail";

import { useEditor, EditorContent } from "@tiptap/react";
import Mention from "@tiptap/extension-mention";
import { mergeAttributes } from "@tiptap/core";
import StarterKit from "@tiptap/starter-kit";
import Placeholder from "@tiptap/extension-placeholder";
import "tippy.js/dist/tippy.css";
import { ReactRenderer } from "@tiptap/react";
import tippy from "tippy.js";
import Emoji, { gitHubEmojis } from "@tiptap/extension-emoji";

import Fuse from "fuse.js";
import EmojiPicker from "emoji-picker-react";

import { supabase } from "../../supabaseClient";

const ask = (msg, def = "") => window.prompt(msg, def);
const ok = (msg) => window.confirm(msg);

const emojiSearchList = Array.isArray(gitHubEmojis)
    ? gitHubEmojis
    : Object.values(gitHubEmojis || {});

const emojiFuse = new Fuse(emojiSearchList, {
    // comment: adjust keys depending on your gitHubEmojis shape
    keys: ["name", "shortcodes", "tags", "emoji"],
    threshold: 0.3,
    ignoreLocation: true,
});

// editor toolbar for basic formatting
const NotaEditorToolbar = ({ editor, setShowEmojiPicker }) => {
    if (!editor) return null;

    const isEditable = editor.isEditable;

    const getButtonSx = (isActive) => ({
        borderRadius: 1,
        ...(isActive && {
            backgroundColor: "action.selected",
        }),
    });

    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                flexWrap: "wrap",
                // gap: 0.5,
                borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                pb: 0.5,
                mb: 0.5,
                "& .MuiIconButton-root": {
                    // comment: small hover hint to feel more "buttony"
                    "&:hover": {
                        backgroundColor: (theme) => theme.palette.action.hover,
                    },
                },
            }}
        >
            {/* comment: clear formatting, undo, redo */}
            <Tooltip title="Quitar formatos">
                <span>
                    <IconButton
                        size="small"
                        onClick={() =>
                            editor
                                .chain()
                                .focus()
                                .clearNodes()
                                .unsetAllMarks()
                                .run()
                        }
                        disabled={!isEditable}
                    >
                        <FormatClearIcon fontSize="small" />
                    </IconButton>
                </span>
            </Tooltip>

            <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
            {/* comment: inline text formatting */}
            <Tooltip title="Negrita">
                <span>
                    <IconButton
                        size="small"
                        onClick={() =>
                            editor.chain().focus().toggleBold().run()
                        }
                        disabled={
                            !isEditable ||
                            !editor.can().chain().focus().toggleBold().run()
                        }
                        sx={getButtonSx(editor.isActive("bold"))}
                    >
                        <FormatBoldIcon fontSize="small" />
                    </IconButton>
                </span>
            </Tooltip>

            <Tooltip title="Cursiva">
                <span>
                    <IconButton
                        size="small"
                        onClick={() =>
                            editor.chain().focus().toggleItalic().run()
                        }
                        disabled={
                            !isEditable ||
                            !editor.can().chain().focus().toggleItalic().run()
                        }
                        sx={getButtonSx(editor.isActive("italic"))}
                    >
                        <FormatItalicIcon fontSize="small" />
                    </IconButton>
                </span>
            </Tooltip>

            <Tooltip title="Tachado">
                <span>
                    <IconButton
                        size="small"
                        onClick={() =>
                            editor.chain().focus().toggleStrike().run()
                        }
                        disabled={
                            !isEditable ||
                            !editor.can().chain().focus().toggleStrike().run()
                        }
                        sx={getButtonSx(editor.isActive("strike"))}
                    >
                        <StrikethroughSIcon fontSize="small" />
                    </IconButton>
                </span>
            </Tooltip>

            <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

            {/* comment: block styles (paragraph and headings) */}
            <Tooltip title="Párrafo normal">
                <span>
                    <IconButton
                        size="small"
                        onClick={() =>
                            editor.chain().focus().setParagraph().run()
                        }
                        disabled={
                            !isEditable ||
                            !editor.can().chain().focus().setParagraph().run()
                        }
                        sx={getButtonSx(editor.isActive("paragraph"))}
                    >
                        <TextFieldsIcon fontSize="small" />
                    </IconButton>
                </span>
            </Tooltip>

            <Tooltip title="Título (nivel 1)">
                <span>
                    <IconButton
                        size="small"
                        onClick={() =>
                            editor
                                .chain()
                                .focus()
                                .toggleHeading({ level: 1 })
                                .run()
                        }
                        disabled={
                            !isEditable ||
                            !editor
                                .can()
                                .chain()
                                .focus()
                                .toggleHeading({ level: 1 })
                                .run()
                        }
                        sx={getButtonSx(
                            editor.isActive("heading", { level: 1 })
                        )}
                    >
                        <LooksOneIcon fontSize="small" />
                    </IconButton>
                </span>
            </Tooltip>

            <Tooltip title="Título (nivel 2)">
                <span>
                    <IconButton
                        size="small"
                        onClick={() =>
                            editor
                                .chain()
                                .focus()
                                .toggleHeading({ level: 2 })
                                .run()
                        }
                        disabled={
                            !isEditable ||
                            !editor
                                .can()
                                .chain()
                                .focus()
                                .toggleHeading({ level: 2 })
                                .run()
                        }
                        sx={getButtonSx(
                            editor.isActive("heading", { level: 2 })
                        )}
                    >
                        <LooksTwoIcon fontSize="small" />
                    </IconButton>
                </span>
            </Tooltip>

            <Tooltip title="Título (nivel 3)">
                <span>
                    <IconButton
                        size="small"
                        onClick={() =>
                            editor
                                .chain()
                                .focus()
                                .toggleHeading({ level: 3 })
                                .run()
                        }
                        disabled={
                            !isEditable ||
                            !editor
                                .can()
                                .chain()
                                .focus()
                                .toggleHeading({ level: 3 })
                                .run()
                        }
                        sx={getButtonSx(
                            editor.isActive("heading", { level: 3 })
                        )}
                    >
                        <Looks3Icon fontSize="small" />
                    </IconButton>
                </span>
            </Tooltip>

            {/* comment: horizontal rule (---) */}
            <Tooltip title="Separador (---)">
                <span>
                    <IconButton
                        size="small"
                        onClick={() =>
                            editor.chain().focus().setHorizontalRule().run()
                        }
                        disabled={
                            !isEditable ||
                            !editor
                                .can()
                                .chain()
                                .focus()
                                .setHorizontalRule()
                                .run()
                        }
                    >
                        <HorizontalRuleIcon fontSize="small" />
                    </IconButton>
                </span>
            </Tooltip>

            <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

            {/* comment: lists and quote */}
            <Tooltip title="Lista con viñetas">
                <span>
                    <IconButton
                        size="small"
                        onClick={() =>
                            editor.chain().focus().toggleBulletList().run()
                        }
                        disabled={
                            !isEditable ||
                            !editor
                                .can()
                                .chain()
                                .focus()
                                .toggleBulletList()
                                .run()
                        }
                        sx={getButtonSx(editor.isActive("bulletList"))}
                    >
                        <FormatListBulletedIcon fontSize="small" />
                    </IconButton>
                </span>
            </Tooltip>

            <Tooltip title="Lista numerada">
                <span>
                    <IconButton
                        size="small"
                        onClick={() =>
                            editor.chain().focus().toggleOrderedList().run()
                        }
                        disabled={
                            !isEditable ||
                            !editor
                                .can()
                                .chain()
                                .focus()
                                .toggleOrderedList()
                                .run()
                        }
                        sx={getButtonSx(editor.isActive("orderedList"))}
                    >
                        <FormatListNumberedIcon fontSize="small" />
                    </IconButton>
                </span>
            </Tooltip>

            <Tooltip title="Cita">
                <span>
                    <IconButton
                        size="small"
                        onClick={() =>
                            editor.chain().focus().toggleBlockquote().run()
                        }
                        disabled={
                            !isEditable ||
                            !editor
                                .can()
                                .chain()
                                .focus()
                                .toggleBlockquote()
                                .run()
                        }
                        sx={getButtonSx(editor.isActive("blockquote"))}
                    >
                        <FormatQuoteIcon fontSize="small" />
                    </IconButton>
                </span>
            </Tooltip>

            <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
            <Tooltip title="Mencionar persona (@)">
                <span>
                    <IconButton
                        size="small"
                        onClick={() => {
                            if (!editor) return;
                            // comment: trigger your mention logic (menu or suggestion)
                            // simplest: insert "@" so the Mention suggestion pops up
                            editor.chain().focus().insertContent("@").run();
                        }}
                        disabled={!editor?.isEditable}
                    >
                        <AlternateEmailIcon fontSize="small" />
                    </IconButton>
                </span>
            </Tooltip>
            <Tooltip title="Insertar emoji">
                <span>
                    <IconButton
                        size="small"
                        onClick={() => setShowEmojiPicker((prev) => !prev)}
                        disabled={!editor?.isEditable}
                    >
                        <EmojiEmotionsIcon fontSize="small" />
                    </IconButton>
                </span>
            </Tooltip>

            <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

            <Divider
                orientation="vertical"
                flexItem
                sx={{ mx: 0.5, flexGrow: 1 }}
            />

            <Tooltip title="Deshacer">
                <span>
                    <IconButton
                        size="small"
                        onClick={() => editor.chain().focus().undo().run()}
                        disabled={
                            !isEditable ||
                            !editor.can().chain().focus().undo().run()
                        }
                    >
                        <UndoIcon fontSize="small" />
                    </IconButton>
                </span>
            </Tooltip>

            <Tooltip title="Rehacer">
                <span>
                    <IconButton
                        size="small"
                        onClick={() => editor.chain().focus().redo().run()}
                        disabled={
                            !isEditable ||
                            !editor.can().chain().focus().redo().run()
                        }
                    >
                        <RedoIcon fontSize="small" />
                    </IconButton>
                </span>
            </Tooltip>
            <Tooltip title="Ver atajos de teclado">
                <span>
                    {/* <IconButton size="small" onClick={onShowShortcuts}>
                        <HelpIcon fontSize="small" />
                    </IconButton> */}
                </span>
            </Tooltip>
        </Box>
    );
};

const PersonaMentionList = ({ items, command, selectedIndex }) => {
    const theme = useTheme();
    const safeItems = Array.isArray(items) ? items : []; // comment: ensure items is always an array

    return (
        <Box
            component="ul"
            sx={{
                listStyle: "none",
                bgcolor: theme.palette.background.paper,
                borderRadius: 1,
                boxShadow: 3,
                minWidth: 200,
                maxHeight: 260,
                overflowY: "auto",
            }}
        >
            {safeItems.map((item, index) => (
                <Box
                    key={item.id}
                    component="li"
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        px: 1,
                        py: 0.5,
                        cursor: "pointer",
                        borderRadius: 0.75,
                        bgcolor:
                            index === selectedIndex
                                ? theme.palette.action.selected
                                : "transparent",
                        "&:hover": {
                            bgcolor: theme.palette.action.hover,
                        },
                    }}
                    onMouseDown={(e) => {
                        e.preventDefault();
                        command(item);
                    }}
                >
                    <Avatar
                        sx={{ width: 24, height: 24, fontSize: 12 }}
                        src={item.avatarUrl || undefined}
                        alt={item.label}
                        imgProps={{ referrerPolicy: "no-referrer" }}
                    >
                        {(item && item.label && item.label[0]) || "?"}
                    </Avatar>
                    <Typography
                        variant="body2"
                        sx={{
                            fontWeight: 500,
                            whiteSpace: "nowrap",
                            textOverflow: "ellipsis",
                            overflow: "hidden",
                            maxWidth: 160,
                            color: "text.primary",
                        }}
                    >
                        {item.label}
                    </Typography>
                </Box>
            ))}
        </Box>
    );
};

// comment: helper to insert a mention node for a persona
function insertPersonaMention(editor, persona) {
    if (!editor || !persona) return;

    editor
        .chain()
        .focus()
        .insertContent([
            {
                type: "mention",
                attrs: {
                    id: persona.persona_id, // comment: this is the stable id
                    label: persona.display_name || "Sin nombre",
                },
            },
            {
                type: "text",
                text: " ",
            },
        ])
        .run();
}

// comment: update mention labels using the latest personas list
const hydratePersonaMentions = (html, personas = []) => {
    if (!html || !personas || personas.length === 0) {
        return html;
    }

    if (typeof window === "undefined" || typeof DOMParser === "undefined") {
        return html;
    }

    try {
        const map = new Map(
            personas.map((p) => [
                String(p.persona_id),
                p.display_name || "Sin nombre",
            ])
        );

        const parser = new DOMParser();
        const doc = parser.parseFromString(html, "text/html");

        doc.querySelectorAll('span[data-persona-mention="true"]').forEach(
            (el) => {
                const id =
                    el.getAttribute("data-persona-id") ||
                    el.getAttribute("data-id");
                const name = id ? map.get(String(id)) : null;
                if (!name) return;

                el.textContent = name;
                el.setAttribute("data-label", name);
            }
        );

        return doc.body.innerHTML;
    } catch (e) {
        console.error("hydratePersonaMentions error", e);
        return html;
    }
};

// comment: rich text editor for notes
const NotaEditor = ({
    value,
    onChange,
    readOnly = false,
    minHeight = 120,
    maxHeight = 320,
    onEditorReady,
    personas = [],
    isEditingNota = false,
}) => {
    const theme = useTheme();
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);
    const [emojiSuggestionState, setEmojiSuggestionState] = useState(null);
    const [emojiSearchText, setEmojiSearchText] = useState("");

    const editor = useEditor({
        extensions: [
            StarterKit.configure({
                heading: {
                    levels: [1, 2, 3, 4],
                },
            }),
            Placeholder.configure({
                placeholder: "Escribe tu nota... Usa #, ##, ###, *, 1. etc.",
            }),
            Mention.configure({
                HTMLAttributes: {
                    class: "persona-mention",
                    "data-persona-mention": "true",
                },
                renderHTML({ options, node }) {
                    const id = node.attrs.id;
                    const label = node.attrs.label || id || "persona";

                    // comment: try to enrich mention with avatar using current personas list
                    const persona =
                        (personas || []).find(
                            (p) =>
                                String(p.persona_id) ===
                                String(node.attrs.id || id)
                        ) || null;
                    const avatarUrl = (persona && persona.googlePicURL) || null;

                    const attrs = mergeAttributes(options.HTMLAttributes, {
                        "data-persona-id": id,
                        "data-label": label,
                    });

                    const children = [];

                    if (avatarUrl) {
                        children.push([
                            "span",
                            { class: "persona-mention-avatar" },
                            [
                                "img",
                                {
                                    src: avatarUrl,
                                    alt: label,
                                    referrerpolicy: "no-referrer",
                                },
                            ],
                        ]);
                    }

                    children.push([
                        "span",
                        { class: "persona-mention-label" },
                        label,
                    ]);

                    return ["span", attrs, ...children];
                },

                renderText({ options, node }) {
                    const char =
                        options?.suggestion?.char !== undefined
                            ? options.suggestion.char
                            : "@";
                    const label =
                        node.attrs.label || node.attrs.id || "persona";
                    return `${char}${label}`;
                },
                suggestion: {
                    char: "@",
                    // comment: allow @ at beginning of line or after any character
                    allowedPrefixes: null,
                    items: ({ query }) => {
                        const q = (query || "").toLowerCase();

                        return (personas || [])
                            .filter((p) =>
                                (p.display_name || "").toLowerCase().includes(q)
                            )
                            .slice(0, 8)
                            .map((p) => ({
                                id: p.persona_id,
                                label: p.display_name || "Sin nombre",
                                avatarUrl: p.googlePicURL || null, // comment: thumbnail for suggestion menu
                            }));
                    },
                    render: () => {
                        let reactRenderer;
                        let popup;

                        return {
                            onStart: (props) => {
                                reactRenderer = new ReactRenderer(
                                    PersonaMentionList,
                                    {
                                        props,
                                        editor: props.editor,
                                    }
                                );

                                popup = tippy("body", {
                                    getReferenceClientRect: props.clientRect,
                                    appendTo: () => document.body,
                                    content: reactRenderer.element,
                                    showOnCreate: true,
                                    interactive: true,
                                    hideOnClick: false,
                                })[0];
                            },
                            onUpdate(props) {
                                reactRenderer.updateProps(props);
                                if (!props.clientRect) return;
                                popup.setProps({
                                    getReferenceClientRect: props.clientRect,
                                });
                            },
                            onKeyDown(props) {
                                // comment: allow Escape to close suggestion
                                if (props.event.key === "Escape") {
                                    popup.hide();
                                    return true;
                                }
                                return false;
                            },
                            onExit() {
                                popup?.destroy();
                                reactRenderer?.destroy();
                            },
                        };
                    },
                },
            }),

            Emoji.configure({
                emojis: gitHubEmojis,
                enableEmoticons: true,
                fallbackImageSrc: undefined,
                suggestion: {
                    char: ":",
                    // comment: allow ":" anywhere
                    allowedPrefixes: null,
                    items: ({ query }) => {
                        const q = (query || "").trim();

                        if (!q) {
                            // comment: first page when there is no query
                            return emojiSearchList;
                        }

                        return emojiFuse
                            .search(q)
                            .slice(0, 50)
                            .map((result) => result.item);
                    },
                    render: () => {
                        return {
                            onStart: (props) => {
                                const q = (props.query || "").trim();

                                // comment: keep latest ":" query and range
                                setEmojiSearchText(q);
                                setEmojiSuggestionState({
                                    editor: props.editor,
                                    range: props.range,
                                    query: q,
                                });

                                // comment: open EmojiPicker instead of internal list
                                setShowEmojiPicker(true);
                            },
                            onUpdate: (props) => {
                                const q = (props.query || "").trim();

                                setEmojiSearchText(q);
                                setEmojiSuggestionState((prev) => {
                                    if (!prev) return prev;
                                    return {
                                        ...prev,
                                        editor: props.editor,
                                        range: props.range,
                                        query: q,
                                    };
                                });
                            },
                            onKeyDown: (props) => {
                                // comment: let Escape close picker / suggestion
                                if (props.event.key === "Escape") {
                                    setShowEmojiPicker(false);
                                    return true;
                                }
                                return false;
                            },
                            onExit: () => {
                                // comment: reset state when suggestion ends
                                setShowEmojiPicker(false);
                                setEmojiSuggestionState(null);
                                setEmojiSearchText("");
                            },
                        };
                    },
                },
            }),
        ],
        content: value || "",
        editable: !readOnly,
        onUpdate: ({ editor }) => {
            if (onChange) {
                const html = editor.getHTML();
                onChange(html);
            }
        },
    });

    useEffect(() => {
        if (!editor || !onEditorReady) return;
        onEditorReady(editor);
    }, [editor, onEditorReady]);

    // comment: sync TipTap editable state with readOnly prop
    useEffect(() => {
        if (!editor) return;
        editor.setEditable(!readOnly);
    }, [editor, readOnly]);

    // comment: sync TipTap content with external value
    useEffect(() => {
        if (!editor) return;
        const current = editor.getHTML();
        const safeValue = value || "";
        if (current !== safeValue) {
            editor.commands.setContent(safeValue, false);
        }
    }, [value, editor]);

    return (
        <Box
            sx={{
                mt: 1,
                // comment: no scroll here, this keeps toolbar fixed
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 1,
                px: { xs: 1, sm: 1.5 }, // comment: reduce side padding on mobile
                py: { xs: 0.75, sm: 1 },
                typography: "body2",
                backgroundColor: theme.palette.background.paper,
                position: "relative", // comment: for absolute emoji picker
                "&:focus-within": {
                    borderColor: theme.palette.primary.main,
                    boxShadow: `0 0 0 1px ${theme.palette.primary.main}33`,
                },
                "& .tiptap": {
                    outline: "none",
                },
                // comment: list styling
                "& .tiptap ul, & .tiptap ol": {
                    paddingLeft: theme.spacing(3),
                    margin: "0 0 0.35em",
                },
                "& .tiptap ul": {
                    listStyle: "disc",
                },
                "& .tiptap ol": {
                    listStyle: "decimal",
                },
                "& .tiptap li": {
                    marginBottom: "0.15em",
                },
                // comment: persona mention styles
                "& .persona-mention": {
                    display: "inline-flex",
                    alignItems: "center",
                    verticalAlign: "middle",
                    borderRadius: 999,
                    padding: "0 6px",
                    margin: "0 1px 1px",
                    backgroundColor: theme.palette.action.selected,
                    color: theme.palette.primary.main,
                    fontWeight: 500,
                    border: `1px solid ${theme.palette.primary.main}33`,
                    lineHeight: 1.4,
                    maxWidth: "100%",
                },
                "& .persona-mention-avatar": {
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginRight: 1,
                    flexShrink: 0,
                },
                "& .persona-mention-avatar img": {
                    width: 18,
                    height: 18,
                    borderRadius: "50%",
                    display: "block",
                },
                "& .persona-mention-label": {
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                },
            }}
        >
            {showEmojiPicker && (
                <Box
                    sx={{
                        position: "absolute",
                        zIndex: (theme) => theme.zIndex.modal + 10,
                    }}
                >
                    <EmojiPicker
                        emojiStyle="native"
                        skinTonesDisabled={false}
                        onEmojiClick={(emojiData /* , event */) => {
                            if (!editor) return;

                            // insert emoji at current cursor position
                            editor
                                .chain()
                                .focus()
                                .insertContent(emojiData.emoji)
                                .run();

                            // close emoji picker after inserting
                            setShowEmojiPicker(false);
                        }}
                        searchPlaceHolder="Buscar emoji…"
                        lazyLoadEmojis
                        previewConfig={{ showPreview: false }}
                    />
                </Box>
            )}

            {/* comment: toolbar stays fixed at the top */}
            {isEditingNota && (
                <Box
                    sx={{
                        position: "sticky",
                        top: 0,
                        zIndex: 3,
                        backgroundColor: theme.palette.background.paper,
                    }}
                >
                    <NotaEditorToolbar
                        editor={editor}
                        setShowEmojiPicker={setShowEmojiPicker}
                    />
                </Box>
            )}

            {/* comment: only the content area scrolls */}
            <Box
                sx={{
                    mt: 1,
                    minHeight,
                    ...(maxHeight && {
                        maxHeight,
                        overflowY: "auto",
                    }),
                }}
            >
                <EditorContent editor={editor} className="tiptap" />
            </Box>
        </Box>
    );
};

// comment: list item that shows a single note preview
const Nota = ({ nota, handleOpenNota, personas = [] }) => {
    const displayDate = nota.updated_at || nota.created_at;
    const rawContenido = nota.contenido || "";
    const contenido = useMemo(
        () => hydratePersonaMentions(rawContenido, personas),
        [rawContenido, personas]
    );
    const isHtmlContent = contenido.trim().startsWith("<");

    return (
        <ListItem
            key={nota.nota_id}
            disableGutters
            sx={{
                mb: 0.5,
                px: 1,
                cursor: "pointer",
                borderRadius: 1,
                "&:hover": {
                    backgroundColor: "action.hover",
                },
            }}
            onClick={() => handleOpenNota(nota)}
        >
            <Stack direction="column" spacing={0.25} sx={{ width: "100%" }}>
                <Typography variant="caption" color="text.secondary">
                    {displayDate ? new Date(displayDate).toLocaleString() : ""}
                </Typography>
                <Typography
                    variant="subtitle2"
                    noWrap
                    sx={{
                        maxWidth: "100%",
                        fontWeight: 600,
                        color: "#36456DFF",
                    }}
                >
                    {nota.titulo || "(Sin título)"}
                </Typography>

                {isHtmlContent ? (
                    <Box
                        sx={{
                            maxHeight: 72,
                            overflow: "hidden",
                            display: "-webkit-box",
                            WebkitLineClamp: 3,
                            WebkitBoxOrient: "vertical",
                            "& p": {
                                margin: 0,
                                marginBottom: "0.2rem",
                            },
                            "& h1, & h2, & h3, & h4": {
                                margin: 0,
                                marginBottom: "0.2rem",
                                fontSize: "0.9rem",
                            },
                        }}
                        dangerouslySetInnerHTML={{ __html: contenido }}
                    />
                ) : (
                    <Typography
                        variant="body2"
                        sx={{
                            maxHeight: 72,
                            overflow: "hidden",
                            display: "-webkit-box",
                            WebkitLineClamp: 3,
                            WebkitBoxOrient: "vertical",
                            whiteSpace: "pre-wrap",
                        }}
                    >
                        {contenido}
                    </Typography>
                )}
            </Stack>
        </ListItem>
    );
};

// comment: main notes section used inside PersonaDrawer
const PersonaNotasSection = ({ personaId, persona, open, personas = [] }) => {
    const [notas, setNotas] = useState([]);
    const [loadingNotas, setLoadingNotas] = useState(false);
    const [selectedNota, setSelectedNota] = useState(null);
    const [openNotaDialog, setOpenNotaDialog] = useState(false);
    const [isCreatingNota, setIsCreatingNota] = useState(false);
    const [isEditingNota, setIsEditingNota] = useState(false);
    const [editTitulo, setEditTitulo] = useState("");
    const [editContenido, setEditContenido] = useState("");
    const [sortField, setSortField] = useState("created_at"); // "created_at" | "updated_at"
    const [sortDirection, setSortDirection] = useState("desc"); // "asc" | "desc"
    const [sortMenuAnchor, setSortMenuAnchor] = useState(null);
    const [searchQuery, setSearchQuery] = useState("");
    const [isSearchOpen, setIsSearchOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const [notaEditor, setNotaEditor] = useState(null);
    const [mentionMenuAnchor, setMentionMenuAnchor] = useState(null);
    const theme = useTheme(); // comment: get theme for breakpoints
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));

    const handleToggleSearch = () => {
        setIsSearchOpen((prev) => {
            const next = !prev;
            if (!next) {
                setSearchQuery("");
            }
            return next;
        });
    };

    const handleOpenSortMenu = (event) => {
        setSortMenuAnchor(event.currentTarget);
    };

    const handleCloseSortMenu = () => {
        setSortMenuAnchor(null);
    };

    const handleChangeSort = (field, direction) => {
        setSortField(field);
        setSortDirection(direction);
        handleCloseSortMenu();
    };

    const isCurrentSort = (field, direction) =>
        sortField === field && sortDirection === direction;

    const loadNotas = useCallback(async () => {
        if (!personaId) {
            setNotas([]);
            return;
        }
        try {
            setLoadingNotas(true);
            const { data, error } = await supabase
                .from("Notas")
                .select(
                    "nota_id, created_at, updated_at, titulo, contenido, tags"
                )
                .eq("persona_id", personaId)
                .order(sortField, {
                    ascending: sortDirection === "asc",
                });

            if (error) throw error;
            setNotas(data || []);
        } catch (e) {
            console.error("Error loading notas:", e);
        } finally {
            setLoadingNotas(false);
        }
    }, [personaId, sortField, sortDirection]);

    const filteredNotas = useMemo(() => {
        if (!searchQuery || !searchQuery.trim()) {
            return notas;
        }

        const fuse = new Fuse(notas, {
            keys: ["titulo", "contenido", "tags"],
            threshold: 0.3,
            ignoreLocation: true,
        });

        return fuse.search(searchQuery.trim()).map((result) => result.item);
    }, [notas, searchQuery]);

    useEffect(() => {
        if (!open) return;
        loadNotas();
    }, [open, loadNotas]);

    const handleCreateNota = async ({ titulo, contenido }) => {
        if (!personaId || !persona) return;

        const trimmedTitulo = (titulo || "").trim();
        const trimmedContenido = (contenido || "").trim();

        if (!trimmedTitulo && !trimmedContenido) return;

        try {
            setSaving(true);
            const payload = {
                persona_id: personaId,
                owner_id: persona.owner_id,
                contenido: trimmedContenido,
            };
            if (trimmedTitulo) {
                payload.titulo = trimmedTitulo;
            }

            const { error } = await supabase.from("Notas").insert(payload);
            if (error) throw error;

            await loadNotas();
        } catch (e) {
            alert(e?.message || "No se pudo crear la nota.");
            throw e;
        } finally {
            setSaving(false);
        }
    };

    const handleOpenNota = (nota) => {
        setSelectedNota(nota);
        setEditTitulo(nota?.titulo || "");

        const hydrated = hydratePersonaMentions(
            nota?.contenido || "",
            personas
        );
        setEditContenido(hydrated);

        setIsCreatingNota(false);
        setIsEditingNota(false);
        setNotaEditor(null);
        setOpenNotaDialog(true);
    };

    const handleOpenMentionMenu = (event) => {
        setMentionMenuAnchor(event.currentTarget);
    };

    const handleCloseMentionMenu = () => {
        setMentionMenuAnchor(null);
    };

    const handleInsertMention = (personaToMention) => {
        if (!notaEditor || !personaToMention) {
            setMentionMenuAnchor(null);
            return;
        }

        // usamos el helper que inserta el nodo "mention" correcto
        insertPersonaMention(notaEditor, personaToMention);

        setMentionMenuAnchor(null);
    };

    const handleCloseNotaDialog = () => {
        setOpenNotaDialog(false);
        setSelectedNota(null);
        setIsEditingNota(false);
        setIsCreatingNota(false);
        setEditTitulo("");
        setEditContenido("");
    };

    const handleStartEditNota = () => {
        if (!selectedNota) return;
        setEditTitulo(selectedNota.titulo || "");
        setEditContenido(selectedNota.contenido || "");
        setIsEditingNota(true);
    };

    // comment: open dialog in "new note" mode
    const handleStartCreateNota = () => {
        setSelectedNota(null);
        setEditTitulo("");
        setEditContenido("");
        setIsCreatingNota(true);
        setIsEditingNota(true);
        setOpenNotaDialog(true);
    };

    const handleCancelEditNota = () => {
        if (isCreatingNota) {
            setOpenNotaDialog(false);
            setIsCreatingNota(false);
            setIsEditingNota(false);
            setEditTitulo("");
            setEditContenido("");
            return;
        }

        if (selectedNota) {
            setEditTitulo(selectedNota.titulo || "");
            setEditContenido(selectedNota.contenido || "");
        } else {
            setEditTitulo("");
            setEditContenido("");
        }
        setIsEditingNota(false);
    };

    const handleSaveNota = async () => {
        if (!selectedNota) return;

        const trimmedTitulo = (editTitulo || "").trim();
        const trimmedContenido = (editContenido || "").trim();

        if (!trimmedTitulo && !trimmedContenido) return;

        try {
            setSaving(true);

            const payload = {
                contenido: trimmedContenido,
                titulo: trimmedTitulo || null,
                updated_at: new Date().toISOString(),
            };

            const { error } = await supabase
                .from("Notas")
                .update(payload)
                .eq("nota_id", selectedNota.nota_id);

            if (error) throw error;

            setSelectedNota((prev) =>
                prev
                    ? {
                          ...prev,
                          titulo: trimmedTitulo || null,
                          contenido: trimmedContenido,
                          updated_at: payload.updated_at,
                      }
                    : prev
            );

            await loadNotas();
            setIsEditingNota(false);
        } catch (e) {
            alert(e?.message || "No se pudo actualizar la nota.");
        } finally {
            setSaving(false);
        }
    };

    // comment: create new note from dialog using current editTitulo/editContenido
    const handleSaveNewNota = async () => {
        const trimmedTitulo = (editTitulo || "").trim();
        const trimmedContenido = (editContenido || "").trim();

        if (!trimmedTitulo && !trimmedContenido) return;

        try {
            await handleCreateNota({
                titulo: trimmedTitulo,
                contenido: trimmedContenido,
            });

            setOpenNotaDialog(false);
            setIsCreatingNota(false);
            setIsEditingNota(false);
            setEditTitulo("");
            setEditContenido("");
        } catch (e) {
            console.error("Error creating note from dialog:", e);
        }
    };

    const handleDeleteNota = async () => {
        if (!selectedNota || isCreatingNota) return;
        if (!ok("¿Borrar esta nota?")) return;

        try {
            setSaving(true);
            const { error } = await supabase
                .from("Notas")
                .delete()
                .eq("nota_id", selectedNota.nota_id);
            if (error) throw error;
            setOpenNotaDialog(false);
            setSelectedNota(null);
            await loadNotas();
        } catch (e) {
            alert(e?.message || "No se pudo borrar la nota.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <>
            {/* --- Notes section header and actions --- */}
            <Box
                sx={{
                    px: 2,
                    pb: 1,
                    pt: 2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                }}
            >
                <Typography variant="subtitle" sx={{ flexGrow: 1 }}>
                    Notas
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <Tooltip
                        title={
                            isSearchOpen
                                ? "Ocultar búsqueda"
                                : "Buscar en notas"
                        }
                        arrow
                    >
                        <span>
                            <IconButton
                                size="small"
                                onClick={handleToggleSearch}
                                disabled={saving || loadingNotas}
                                aria-label="search-notes"
                            >
                                <SearchIcon fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                    <Tooltip title="Ordenar notas" arrow>
                        <span>
                            <IconButton
                                size="small"
                                onClick={handleOpenSortMenu}
                                disabled={saving || loadingNotas}
                                aria-label="sort-notes"
                            >
                                <SortIcon fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                    <Tooltip title="Nueva nota" arrow>
                        <span>
                            <IconButton
                                size="small"
                                onClick={handleStartCreateNota}
                                disabled={saving}
                                aria-label="add-note"
                            >
                                <AddIcon fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                </Box>
            </Box>

            {isSearchOpen && (
                <Box sx={{ px: 2, pb: 1 }}>
                    <TextField
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        size="small"
                        fullWidth
                        placeholder="Buscar en título, contenido o tags"
                    />
                </Box>
            )}

            {/* --- Sort menu --- */}
            <Menu
                anchorEl={sortMenuAnchor}
                open={Boolean(sortMenuAnchor)}
                onClose={handleCloseSortMenu}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
            >
                <MenuItem
                    onClick={() => handleChangeSort("created_at", "desc")}
                >
                    <ListItemIcon>
                        {isCurrentSort("created_at", "desc") && (
                            <CircleIcon fontSize="small" />
                        )}
                    </ListItemIcon>
                    <Typography variant="body2">
                        Creada (más recientes primero)
                    </Typography>
                </MenuItem>
                <MenuItem onClick={() => handleChangeSort("created_at", "asc")}>
                    <ListItemIcon>
                        {isCurrentSort("created_at", "asc") && (
                            <CircleIcon fontSize="small" />
                        )}
                    </ListItemIcon>
                    <Typography variant="body2">
                        Creada (más antiguas primero)
                    </Typography>
                </MenuItem>
                <Divider />
                <MenuItem
                    onClick={() => handleChangeSort("updated_at", "desc")}
                >
                    <ListItemIcon>
                        {isCurrentSort("updated_at", "desc") && (
                            <CircleIcon fontSize="small" />
                        )}
                    </ListItemIcon>
                    <Typography variant="body2">
                        Actualizada (más recientes primero)
                    </Typography>
                </MenuItem>
                <MenuItem onClick={() => handleChangeSort("updated_at", "asc")}>
                    <ListItemIcon>
                        {isCurrentSort("updated_at", "asc") && (
                            <CircleIcon fontSize="small" />
                        )}
                    </ListItemIcon>
                    <Typography variant="body2">
                        Actualizada (más antiguas primero)
                    </Typography>
                </MenuItem>
            </Menu>

            {/* --- Notes list --- */}
            <List
                dense
                sx={{
                    px: 1,
                    pb: 1,
                    // maxHeight: 260,
                    overflowY: "auto",
                }}
            >
                {loadingNotas && (
                    <Box sx={{ px: 2, py: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                            Cargando notas...
                        </Typography>
                    </Box>
                )}

                {!loadingNotas && notas.length === 0 && (
                    <Box sx={{ px: 2, py: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                            Sin notas todavía.
                        </Typography>
                    </Box>
                )}

                {!loadingNotas &&
                    notas.length > 0 &&
                    filteredNotas.length === 0 && (
                        <Box sx={{ px: 2, py: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                                No hay notas que coincidan con la búsqueda.
                            </Typography>
                        </Box>
                    )}

                {!loadingNotas &&
                    filteredNotas.map((nota) => (
                        <Nota
                            key={nota.nota_id}
                            nota={nota}
                            handleOpenNota={handleOpenNota}
                            personas={personas || []}
                        />
                    ))}
            </List>

            {/* --- Note dialog --- */}
            <Dialog
                open={openNotaDialog}
                onClose={handleCloseNotaDialog}
                fullWidth
                maxWidth={isMobile ? "lg" : "md"} // comment: a bit wider on mobile if not fullScreen
                fullScreen={isMobile} // comment: full screen when on mobile
                PaperProps={{
                    sx: isMobile
                        ? {
                              m: 0,
                              borderRadius: 0,
                              height: "100dvh", // comment: use dynamic viewport
                              maxHeight: "100dvh",
                              display: "flex",
                              flexDirection: "column",
                          }
                        : {},
                }}
            >
                <DialogTitle>
                    {isCreatingNota
                        ? "Nueva nota"
                        : isEditingNota
                        ? "Editar nota"
                        : selectedNota?.titulo || "Nota"}
                </DialogTitle>

                <DialogContent
                    dividers={!isMobile}
                    sx={{
                        overflow: "visible",
                        p: isMobile ? 1 : 3, // comment: less padding on mobile, more on desktop
                    }}
                >
                    <Stack spacing={1}>
                        {!isCreatingNota && selectedNota && (
                            <Typography
                                variant="caption"
                                color="text.secondary"
                            >
                                {selectedNota.created_at
                                    ? new Date(
                                          selectedNota.created_at
                                      ).toLocaleString()
                                    : ""}{" "}
                                -{" "}
                                {selectedNota.updated_at
                                    ? new Date(
                                          selectedNota.updated_at
                                      ).toLocaleString()
                                    : ""}
                            </Typography>
                        )}

                        {isEditingNota && (
                            <TextField
                                label="Título"
                                fullWidth
                                size="small"
                                value={editTitulo}
                                onChange={(e) => setEditTitulo(e.target.value)}
                            />
                        )}

                        {isEditingNota ? (
                            <NotaEditor
                                value={editContenido}
                                onChange={setEditContenido}
                                readOnly={false}
                                minHeight={180}
                                maxHeight={400}
                                onEditorReady={setNotaEditor}
                                personas={personas}
                                isEditingNota={isEditingNota}
                            />
                        ) : (
                            <NotaEditor
                                value={selectedNota?.contenido || ""}
                                readOnly={true}
                                minHeight={120}
                                maxHeight={400}
                                personas={personas}
                                isEditingNota={isEditingNota}
                            />
                        )}
                    </Stack>
                    <Menu
                        anchorEl={mentionMenuAnchor}
                        open={Boolean(mentionMenuAnchor)}
                        onClose={handleCloseMentionMenu}
                        anchorOrigin={{
                            vertical: "bottom",
                            horizontal: "right",
                        }}
                        transformOrigin={{
                            vertical: "top",
                            horizontal: "right",
                        }}
                    >
                        {/* comment: aquí de momento uso solo la persona actual */}
                        {persona ? (
                            <MenuItem
                                onClick={() => handleInsertMention(persona)}
                            >
                                {persona.display_name || "Sin nombre"}
                            </MenuItem>
                        ) : (
                            <MenuItem disabled>
                                No hay persona seleccionada
                            </MenuItem>
                        )}
                    </Menu>
                </DialogContent>

                <DialogActions>
                    {!isCreatingNota && selectedNota && (
                        <Button
                            color="error"
                            onClick={handleDeleteNota}
                            disabled={saving}
                        >
                            Borrar
                        </Button>
                    )}

                    {isEditingNota ? (
                        <>
                            <Button
                                onClick={handleCancelEditNota}
                                disabled={saving}
                            >
                                Cancelar
                            </Button>
                            <Button
                                onClick={
                                    isCreatingNota
                                        ? handleSaveNewNota
                                        : handleSaveNota
                                }
                                disabled={
                                    saving ||
                                    (!editTitulo.trim() &&
                                        !editContenido.trim())
                                }
                            >
                                Guardar
                            </Button>
                        </>
                    ) : (
                        <Button
                            onClick={handleStartEditNota}
                            disabled={saving || !selectedNota}
                            startIcon={<AddIcon sx={{ display: "none" }} />}
                        >
                            Editar
                        </Button>
                    )}

                    <Button onClick={handleCloseNotaDialog} disabled={saving}>
                        Cerrar
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default PersonaNotasSection;
